import React from 'react';
import { FileTextIcon, TruckIcon, CalendarIcon, BuildingIcon, MapPinIcon, DollarSignIcon, UserIcon } from 'lucide-react';
import { PurchaseOrder } from './ReceiptingView';
import { formatCurrency } from '../utils/currencyFormatter';
import { StatusBadge } from '../StatusBadge';
interface ReceiptHeaderProps {
  purchaseOrder: PurchaseOrder;
  locale?: string;
}
export function ReceiptHeader({
  purchaseOrder,
  locale = 'en-US'
}: ReceiptHeaderProps) {
  // Get status badge type based on PO status
  const getStatusBadgeType = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'confirmed':
        return 'success';
      case 'pending approval':
      case 'pending confirmation':
        return 'pending';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14363">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14364">
        <div className="flex justify-between items-center" data-id="element-14365">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14366">
            <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14367" />
            Purchase Order Details
          </h3>
          <StatusBadge status={getStatusBadgeType(purchaseOrder.status)} text={purchaseOrder.status} size="md" data-id="element-14368" />
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-14369">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-14370">
          <div data-id="element-14371">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14372">
              Purchase Order Information
            </h4>
            <div className="space-y-3" data-id="element-14373">
              <div className="flex items-start" data-id="element-14374">
                <div className="flex-shrink-0" data-id="element-14375">
                  <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-14376" />
                </div>
                <div className="ml-3" data-id="element-14377">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14378">
                    {purchaseOrder.id}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14379">{purchaseOrder.name}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14380">
                <div className="flex-shrink-0" data-id="element-14381">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-14382" />
                </div>
                <div className="ml-3" data-id="element-14383">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14384">
                    Order Date
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14385">{purchaseOrder.date}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14386">
                <div className="flex-shrink-0" data-id="element-14387">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-14388" />
                </div>
                <div className="ml-3" data-id="element-14389">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14390">
                    Total Value
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14391">
                    {formatCurrency(purchaseOrder.total, purchaseOrder.currency, locale)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14392">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14393">
              Vendor Information
            </h4>
            <div className="space-y-3" data-id="element-14394">
              <div className="flex items-start" data-id="element-14395">
                <div className="flex-shrink-0" data-id="element-14396">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-14397" />
                </div>
                <div className="ml-3" data-id="element-14398">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14399">Vendor</p>
                  <p className="text-sm text-gray-500" data-id="element-14400">
                    {purchaseOrder.vendor}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14401">
                <div className="flex-shrink-0" data-id="element-14402">
                  <TruckIcon className="h-5 w-5 text-gray-400" data-id="element-14403" />
                </div>
                <div className="ml-3" data-id="element-14404">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14405">
                    Delivery Status
                  </p>
                  <div className="mt-1" data-id="element-14406">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-14407">
                      Partially Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14408">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14409">
              Shipping Information
            </h4>
            <div className="space-y-3" data-id="element-14410">
              <div className="flex items-start" data-id="element-14411">
                <div className="flex-shrink-0" data-id="element-14412">
                  <BuildingIcon className="h-5 w-5 text-gray-400" data-id="element-14413" />
                </div>
                <div className="ml-3" data-id="element-14414">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14415">Entity</p>
                  <p className="text-sm text-gray-500" data-id="element-14416">
                    {purchaseOrder.entity}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14417">
                <div className="flex-shrink-0" data-id="element-14418">
                  <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-14419" />
                </div>
                <div className="ml-3" data-id="element-14420">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14421">Site</p>
                  <p className="text-sm text-gray-500" data-id="element-14422">{purchaseOrder.site}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}